import { ScrollReset } from '@cigam/template/dist/components';
import { ThemeProvider, StyledEngineProvider } from '@mui/material';
import { createBrowserHistory } from 'history';
import { SnackbarProvider } from 'notistack';
import React, { useCallback, useEffect, useState, FC } from 'react';
import Helmet from 'react-helmet';
import { Router } from 'react-router-dom';
import { useSettings } from '@cigam/template/dist/hooks/useSettings';
import { createTheme } from '@cigam/template/dist/theme';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  runtimeConfigs,
  runtimeNLConfigs,
  getNLFromAPI,
} from 'src/config/runtimeConfigs';
import { navConfig } from 'src/config/navigation';
import Routes from 'src/Routes';
import { isAuthenticated } from 'src/Areas/Genericos/GE/services/authService';
import { loadAllConfiguracoesSistema } from 'src/Areas/Genericos/GE/services/configuracoesSistemaService';
import axios from 'src/utils/axios';
import axiosDebug from 'src/utils/axiosDebug';
import { Auth } from './components/Auth';

const history = createBrowserHistory();

const useStyles = makeStyles(() =>
  createStyles({
    '@global': {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0,
      },
      html: {
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        height: '100%',
        width: '100%',
      },
      body: {
        height: '100%',
        width: '100%',
      },
      '#root': {
        height: '100%',
        width: '100%',
      },
    },
  })
);

const collectNL = (items: any[]): string[] => {
  return items.flatMap(item => [
    ...(item.nomeLogico ? [item.nomeLogico] : []),
    ...(item.items ? collectNL(item.items) : []),
  ]);
};

const getAllNLFromNavigation = (): string[] => {
  const nomes = navConfig.flatMap(module =>
    module.nav.flatMap(section => collectNL(section.items))
  );

  return [...new Set(nomes)];
};

const App: FC = () => {
  const { settings, saveSettings } = useSettings();
  const [render, setRender] = useState(false);

  useStyles();

  const searchEnviroment = useCallback(
    async () => {
      const enviroment = await runtimeConfigs();

      if (enviroment) {
        saveSettings({
          ...settings,
          apiUrl: enviroment.API_URL || null,
          cdnUrl: enviroment.CDN_URL || null,
          cdnKey: enviroment.CDN_KEY || 1,
          apmMonitorUrl: enviroment.APM_MONITOR_URL || null,
          themes: enviroment.THEMES || null,
          theme:
            settings.theme !== 'CIGAM'
              ? settings.theme
              : enviroment.DEFAULT_THEME,
          portalBpmUrl: enviroment.PORTAL_SERVICOS_URL,
          activePortals: enviroment.ACTIVE_PORTALS || null,
          defaultPortal: enviroment.DEFAULT_PORTAL || null,
          defaultLogin: enviroment.DEFAULT_LOGIN || null,
          pdUrl: enviroment.PD_URL || null,
          biUrl: enviroment.BI_URL || null,
          appServerUrl: enviroment.APP_SERVER_URL || null,
          appServerApplication: enviroment.APP_SERVER_APPLICATION || null,
          cryptographyKey: enviroment.CRYPTOGRAPHY_KEY || 16667,
          cryptographyTimeout: enviroment.CRYPTOGRAPHY_TIMEOUT || 90,
          menuMaxAcessos: enviroment.MENU_MAX_ACESSOS || 50,
        });

        if (process.env.NODE_ENV || process.env.NODE_ENV !== 'development') {
          axios.defaults.baseURL = `${enviroment.API_URL}/api`;
          axiosDebug.defaults.baseURL = `${enviroment.API_URL}/apm`;
        }
      }

      return enviroment;
    },
    [settings, saveSettings] // OK
  );

  useEffect(() => {
    const load = async () => {
      // Procura primeiro os nomes lógicos do arquivo config.json.
      await runtimeNLConfigs();

      // Se não existir os nomes lógicos, busca da API.
      const nls = getAllNLFromNavigation();
      await getNLFromAPI(nls);

      const enviroment = await searchEnviroment();
      if (enviroment?.API_URL && isAuthenticated()) {
        loadAllConfiguracoesSistema();
      }
      setRender(true);
    };
    if (!render) {
      load();
    }
  }, [render, searchEnviroment]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: 'pt-BR' }} />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={createTheme(settings)}>
          {/* @ts-ignore */}
          <SnackbarProvider maxSnack={10} autoHideDuration={3000}>
            <Router history={history}>
              <Auth>
                <ScrollReset />
                <Routes />
              </Auth>
            </Router>
          </SnackbarProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
};

export default App;
